import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RefreshCw, Filter } from 'lucide-react';
import './SubscriptionsManagement.css';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SubscriptionsManagement = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();

  const statuses = ['active', 'paused', 'cancelled'];

  useEffect(() => {
    fetchSubscriptions();
  }, [statusFilter]);

  const fetchSubscriptions = async () => {
    try {
      const url = statusFilter 
        ? `${API}/api/admin/subscriptions?status=${statusFilter}`
        : `${API}/api/admin/subscriptions`;
      const response = await axios.get(url, { withCredentials: true });
      setSubscriptions(response.data.subscriptions);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        alert('Admin access required');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateSubscriptionStatus = async (subscriptionId, newStatus) => {
    if (!window.confirm(`Are you sure you want to ${newStatus} this subscription?`)) return;

    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      await axios.put(`${API}/api/admin/subscriptions/${subscriptionId}/status`, formData, {
        withCredentials: true
      });
      alert('Subscription status updated!');
      fetchSubscriptions();
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to update subscription status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: '#22c55e',
      paused: '#f59e0b',
      cancelled: '#ef4444'
    };
    return colors[status] || '#6b7280';
  };

  if (loading) {
    return <div className="admin-loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="subscriptions-management">
      <div className="page-header">
        <h1>Subscriptions Management</h1>
        <div className="filter-section">
          <Filter size={20} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Subscriptions</option>
            {statuses.map(status => (
              <option key={status} value={status}>{status.toUpperCase()}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="subscriptions-grid">
        {subscriptions.length === 0 ? (
          <div className="empty-state">
            <RefreshCw size={64} />
            <p>No subscriptions found</p>
          </div>
        ) : (
          subscriptions.map(sub => (
            <div key={sub.id} className="subscription-card-admin">
              <div className="subscription-header">
                <h3>{sub.productName || 'Monthly Subscription'}</h3>
                <span className="status-badge" style={{ background: getStatusColor(sub.status) }}>
                  {sub.status}
                </span>
              </div>

              <div className="subscription-details">
                <p><strong>Customer:</strong> {sub.email}</p>
                <p><strong>Price:</strong> ₹{sub.price}/month</p>
                <p><strong>Started:</strong> {new Date(sub.startDate || sub.createdAt).toLocaleDateString()}</p>
                {sub.nextDeliveryDate && (
                  <p><strong>Next Delivery:</strong> {new Date(sub.nextDeliveryDate).toLocaleDateString()}</p>
                )}
                <p><strong>Payment:</strong> {sub.paymentStatus}</p>
              </div>

              <div className="subscription-actions">
                {sub.status === 'active' && (
                  <button
                    className="action-btn pause"
                    onClick={() => updateSubscriptionStatus(sub.id, 'paused')}
                  >
                    Pause
                  </button>
                )}
                {sub.status === 'paused' && (
                  <button
                    className="action-btn resume"
                    onClick={() => updateSubscriptionStatus(sub.id, 'active')}
                  >
                    Resume
                  </button>
                )}
                {sub.status !== 'cancelled' && (
                  <button
                    className="action-btn cancel"
                    onClick={() => updateSubscriptionStatus(sub.id, 'cancelled')}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SubscriptionsManagement;